<template>
    <v-dialog
        :value="value"
        max-width="700"
        overlay-color="primary"
        @click:outside="$emit('clickout')"
        :fullscreen="$vuetify.breakpoint.xs"
    >
        <v-card
            width="100%"
            class="fix-card-height"
        >
            <v-btn
                icon
                @click="$emit('input', false)"
                color="primary"
                absolute
                class="close-btn"
                :large="isMobile"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-container class="fix-card-height" fluid>
                <v-row class="fix-card-height">
                    <v-col style="position: relative;" v-if="!isMobile" cols="5">
                        <v-img
                            :src="product.image"
                            :lazy-src="product.thumbnail"
                            :alt="product.name + ' image'"
                            class="product-img"
                            v-if="product.image"
                        >
                        </v-img>
                        <div v-else class="product-img no-img d-flex">
                            <div class="my-auto mx-auto">No product image available.</div>
                        </div>
                    </v-col>
                    <v-col
                        cols="12"
                        class="pt-0"
                        style="margin-top: -.4rem; min-height: 12rem"
                        v-if="isMobile"
                    >
                        <div v-text="product.name" class="product-name"></div>
                        <div style="position: relative" class="h100">
                            <v-img
                                :src="product.image"
                                :lazy-src="product.thumbnail"
                                :alt="product.name + ' image'"
                                class="product-img full-screen"
                                v-if="product.image"
                            >
                            </v-img>
                            <div v-else class="product-img full-screen no-img d-flex">
                                <div class="my-auto mx-auto">No product image available.</div>
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="7" class="d-flex flex-column">
                        <div class="d-flex flex-column">
                            <div v-if="!isMobile" v-text="product.name" class="product-name"></div>
                            <div v-text="product.long_desc" class="mt-4 product-desc"></div>
                            <v-menu max-width="300" open-on-hover offset-y>
                                <template v-slot:activator="{ on }">
                                    <div class="mt-2"><span class="nutrition-link" v-on="on">Nutrition & Ingredients</span></div>
                                </template>
                                <v-card max-width="300">
                                    <v-card-text>
                                        <v-img
                                            v-if="product.nutrition_label"
                                            alt="Nutrition label for this product."
                                            :src="product.nutrition_label"
                                            :lazy-src="product.nutrition_thumb"
                                        ></v-img>
                                        <p class="ma-0" v-else>We're sorry, we don't have nutrition information for this product yet.</p>
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                        </div>
                        <div class="d-flex mt-5">
                            <div class="mr-2" v-for="attribute in product.attributes" :key="attribute.name">
                                <v-img :title="attribute.name" :src="attribute.icon" :alt="attribute.name" height="2.5rem" width="2.5rem"></v-img>
                            </div>
                        </div>
                        <div class="sep mt-4"></div>
                        <div v-if="product.can_add_nuts" class="d-flex flex-row mt-5 mb-5 mb-sm-0">
                            <p class="my-auto">Add Nuts (+$1)</p>
                            <v-checkbox
                                class="my-auto ml-2"
                                dense
                                aria-label="Add Nuts"
                                hide-details
                                style="padding-top: 1px !important"
                                v-model="addNuts"
                            ></v-checkbox>
                            <v-spacer></v-spacer>
                            <v-select
                                v-model="nutsType"
                                style="max-width: 150px;"
                                :items="nutsTypeOptions"
                                v-if="addNuts"
                                hide-details
                                dense
                                outlined
                            ></v-select>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="d-flex flex-row mb-7">
                            <div class="price-label">
                                {{formattedPrice}}
                            </div>
                            <v-spacer></v-spacer>
                            <div class="d-flex flex-column align-center pt-3">
                                <qty-selector v-model="qtySelected"></qty-selector>
                                <span class="price-sublabel">{{product.price_unit}}</span>
                            </div>
                        </div>
                        <v-btn
                            :color="buttonColor"
                            class="atc-btn"
                            :loading="loading"
                            :depressed="loading || flatBtn"
                            rounded
                            @click="addProductsToCart"
                        >{{buttonText}}</v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>
    </v-dialog>
</template>

<script>
    import {formatUSD} from "../../utils/format";
    import QtySelector from "../QtySelector";
    import {mapActions, mapState} from 'vuex';

    export default {
        name: "ProductDetailsCard",
        props: [
            'value', 'product'
        ],
        components: {
            QtySelector
        },
        data(){
            return {
                qtySelected: 1,
                loading: false,
                buttonColor: 'primary',
                buttonText: 'Add to Cart',
                addNuts: false,
                nutsType: 'Walnut'
            }
        },
        methods: {
            ...mapActions({addProductsToCartAction: 'addProductsToCart'}),
            async addProductsToCart(){
                if (this.flatBtn || this.loading) return;
                try{
                    this.loading = true;
                    await this.addProductsToCartAction({
                        productId: this.product.id,
                        qty: this.qtySelected,
                        nuts: this.addNuts,
                        nutsType: this.addNuts ? this.nutsType : null
                    });
                    this.qtySelected = 1;
                    this.loading = false;
                    this.buttonColor = 'success';
                    this.buttonText = 'Success!';
                    setTimeout(() => {
                        this.buttonColor = 'primary';
                        this.buttonText = 'Add to Cart';
                        }, 2000
                    );
                } catch(e){
                    this.loading = false;
                    console.log(e)  // TODO: ERROR HANDLING
                }
            }
        },
        watch:{
            value: function (){
                this.qtySelected = 1
            }
        },
        computed: {
            ...mapState(['nutsTypeOptions']),
            formattedPrice(){
                const price = parseFloat(this.product.price) + (this.addNuts ? 1 : 0)
                return formatUSD(price * this.qtySelected)
            },
            flatBtn(){
                return this.buttonColor === 'success'
            },
            isMobile(){
                return this.$vuetify.breakpoint.xs
            }
        }
    }
</script>

<style scoped>
.product-name{
    color: var(--v-primary-base);
    font-weight: bold;
    font-size: 1.5rem
}

.close-btn{
    top: 2px;
    right: 2px;
}

.product-desc{
    font-size: 1rem;
}

.product-img{
    position: absolute;
    top: 0;
    bottom: 0;
    left: 12px;
    right: 12px;
    border-radius: 5px;
}

.product-img.full-screen{
    left: 0;
    right: 0;
}

.no-img{
    background: var(--v-secondary-base);
    color: var(--v-primary-base);
}

.sep{
    border-bottom: 1px dotted gray;
}

.price-sublabel{
    font-size: .7rem;
}


.atc-btn{
    font-family: "Open Sans", sans-serif;
    color: var(--v-secondary-base) !important;
    text-transform: none;
}

.price-label{
    color: var(--v-primary-base);
    font-size: 2.2rem;
    font-weight: bold;
}

.nutrition-link {
    color: var(--v-primary-base);
    border-bottom: 1px dashed var(--v-primary-base);
    cursor: pointer;
}

.fix-card-height {
    min-height: 450px;
}

@media (max-width: 600px) {
    .fix-card-height {
        min-height: 100vh;
    }
}
</style>